import { useState, useEffect, useCallback, useRef } from 'react'

interface CalendarProps {
  className?: string
  selected?: Date
  onSelect?: (date: Date | undefined) => void
  disabled?: (date: Date) => boolean
  availableDates?: string[] // Array of date strings in ISO format (YYYY-MM-DD)
  defaultMonth?: Date // Month to display initially
}

// Calendar grid is keyed in UTC to match formatDateKey() and the
// slot/availableDates keys produced upstream. See src/utils/date.ts.
const utcMonthStart = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))

function Calendar({
  className = '',
  selected,
  onSelect,
  disabled,
  availableDates = [],
  defaultMonth,
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (defaultMonth) return utcMonthStart(defaultMonth)
    if (selected) return utcMonthStart(selected)
    return utcMonthStart(new Date())
  })

  // Update current month when defaultMonth changes initially
  // Use a ref to prevent resetting during manual navigation
  const hasSetInitialMonth = useRef(false)

  useEffect(() => {
    if (defaultMonth && !hasSetInitialMonth.current) {
      setCurrentMonth(utcMonthStart(defaultMonth))
      hasSetInitialMonth.current = true
    }
  }, [defaultMonth])

  const year = currentMonth.getUTCFullYear()
  const month = currentMonth.getUTCMonth()
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const firstDayOfMonth = new Date(Date.UTC(year, month, 1)).getUTCDay()

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  const isCurrentMonth = useCallback((): boolean => {
    const today = new Date()
    return (
      year === today.getUTCFullYear() && month === today.getUTCMonth()
    )
  }, [year, month])

  const previousMonth = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (isCurrentMonth()) return

    setCurrentMonth(
      (prev) =>
        new Date(Date.UTC(prev.getUTCFullYear(), prev.getUTCMonth() - 1, 1)),
    )
  }, [isCurrentMonth])

  const nextMonth = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setCurrentMonth(
      (prev) =>
        new Date(Date.UTC(prev.getUTCFullYear(), prev.getUTCMonth() + 1, 1)),
    )
  }, [])

  const handleDayClick = (day: number) => {
    const newDate = new Date(Date.UTC(year, month, day))

    if (disabled && disabled(newDate)) {
      return
    }

    onSelect?.(newDate)
  }

  const isSelected = (day: number) => {
    if (!selected) return false
    return (
      selected.getUTCDate() === day &&
      selected.getUTCMonth() === month &&
      selected.getUTCFullYear() === year
    )
  }

  const isToday = (day: number) => {
    const today = new Date()
    return (
      today.getUTCDate() === day &&
      today.getUTCMonth() === month &&
      today.getUTCFullYear() === year
    )
  }

  const dateKey = (day: number) => {
    const m = String(month + 1).padStart(2, '0')
    const d = String(day).padStart(2, '0')
    return `${year}-${m}-${d}`
  }

  const hasAvailability = (day: number) => availableDates.includes(dateKey(day))

  const isDisabled = (day: number) => {
    const date = new Date(Date.UTC(year, month, day))

    if (disabled && disabled(date)) {
      return true
    }

    // Only filter by availability when an availableDates list was actually
    // provided — an empty array (e.g. while the slot query is still loading)
    // must not disable the whole grid.
    if (availableDates.length > 0 && !hasAvailability(day)) {
      return true
    }

    return false
  }

  // Get previous month's trailing days
  const prevMonthDays = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const prevMonthStart = prevMonthDays - firstDayOfMonth + 1

  const days = []

  // Previous month days (greyed out)
  for (let i = 0; i < firstDayOfMonth; i++) {
    const day = prevMonthStart + i
    days.push(
      <div
        key={`prev-${i}`}
        className='h-10 w-10 flex items-center justify-center text-muted-foreground/30 text-base'
      >
        {day}
      </div>
    )
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const selected = isSelected(day)
    const today = isToday(day)
    const available = hasAvailability(day)
    const dayDisabled = isDisabled(day)

    days.push(
      <button
        key={day}
        onClick={() => handleDayClick(day)}
        disabled={dayDisabled}
        className={`relative h-10 w-10 p-0 font-normal text-base transition-all flex flex-col items-center justify-center gap-0.5 ${selected ? 'bg-primary text-primary-foreground rounded-lg font-semibold shadow-sm' : ''} ${!selected && dayDisabled ? 'text-muted-foreground/40 cursor-not-allowed' : ''} ${!selected && !dayDisabled && today ? 'font-semibold text-foreground' : ''} ${!selected && !dayDisabled && !today ? 'text-foreground hover:bg-accent rounded-md' : ''}`.trim()}
      >
        <span>{day}</span>
        {available && !selected && (
          <div className='w-1.5 h-1.5 rounded-full bg-accent' />
        )}
      </button>
    )
  }

  return (
    <div className={`p-4 bg-card rounded-lg border ${className || ''}`.trim()}>
      <div className='space-y-4'>
        <div className='flex justify-between items-center'>
          <button
            type='button'
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              previousMonth(e)
            }}
            disabled={isCurrentMonth()}
            className={`h-10 w-10 bg-transparent p-0 inline-flex items-center justify-center rounded-md transition-colors ${isCurrentMonth() ? 'text-muted-foreground/30 cursor-not-allowed' : 'hover:bg-accent text-muted-foreground'}`.trim()}
            aria-label='Previous month'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='h-4 w-4'
            >
              <path d='m15 18-6-6 6-6' />
            </svg>
          </button>
          <div className='text-sm font-semibold text-foreground'>
            {monthNames[month]} {year}
          </div>
          <button
            type='button'
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              nextMonth(e)
            }}
            className='h-10 w-10 bg-transparent p-0 inline-flex items-center justify-center rounded-md transition-colors hover:bg-accent text-muted-foreground'
            aria-label='Next month'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='h-4 w-4'
            >
              <path d='m9 18 6-6-6-6' />
            </svg>
          </button>
        </div>
        <div className='w-full space-y-2'>
          <div className='grid grid-cols-7 gap-1'>
            {dayNames.map((name) => (
              <div
                key={name}
                className='h-10 flex items-center justify-center text-muted-foreground font-medium text-sm'
              >
                {name}
              </div>
            ))}
          </div>
          <div className='grid grid-cols-7 gap-1'>{days}</div>
        </div>
      </div>
    </div>
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
